// src/VendorTrackingSimple.js
// Simplified Vendor Response Tracking with Manual Status Updates

import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  setDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import './styles.css';

// Status options with colors
const STATUS_OPTIONS = [
  { value: 'not_sent', label: 'Not Sent', color: '#9E9E9E' },
  { value: 'sent', label: 'Sent', color: '#2196F3' },
  { value: 'pending', label: 'Pending Response', color: '#FF9800' },
  { value: 'replied', label: 'Replied', color: '#4CAF50' },
  { value: 'resent', label: 'Resent', color: '#FFC107' },
  { value: 'revision_1', label: 'Revision 1', color: '#9C27B0' },
  { value: 'revision_2', label: 'Revision 2', color: '#673AB7' },
  { value: 'revision_3', label: 'Revision 3', color: '#3F51B5' },
  { value: 'non_responsive', label: 'Non-Responsive', color: '#F44336' },
  { value: 'completed', label: 'Completed', color: '#4CAF50' }
];

export default function VendorTrackingSimple({ tenderId, subsystems, vendors, onUploadQuote }) {
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubsystem, setFilterSubsystem] = useState('all');
  const [editingStatus, setEditingStatus] = useState(null); // { vendorId, subsystemId }
  const [notes, setNotes] = useState({}); // Store notes for each vendor-subsystem pair

  useEffect(() => {
    fetchTrackingData();
  }, [tenderId, subsystems]);

  async function fetchTrackingData() {
    try {
      setLoading(true);
      const data = [];

      for (const subsystem of subsystems) {
        if (!subsystem.assignedVendors || subsystem.assignedVendors.length === 0) {
          continue;
        }

        // Fetch tracking status for each vendor in this subsystem
        for (const vendorId of subsystem.assignedVendors) {
          const vendor = vendors.find(v => v.id === vendorId);
          if (!vendor) continue;

          // Try to fetch existing tracking data
          const trackingDocRef = doc(
            db,
            `tenders/${tenderId}/subsystems/${subsystem.id}/tracking/${vendorId}`
          );
          
          let trackingDoc;
          try {
            const snapshot = await getDocs(
              collection(db, `tenders/${tenderId}/subsystems/${subsystem.id}/tracking`)
            );
            trackingDoc = snapshot.docs.find(d => d.id === vendorId);
          } catch (error) {
            console.log('No tracking data yet for vendor:', vendorId);
          }

          const trackingInfo = trackingDoc ? trackingDoc.data() : {};

          data.push({
            vendorId,
            vendorName: vendor.name,
            vendorEmail: vendor.email,
            subsystemId: subsystem.id,
            subsystemName: subsystem.name,
            status: trackingInfo.status || 'not_sent',
            lastUpdated: trackingInfo.lastUpdated || null,
            notes: trackingInfo.notes || '',
            revision: trackingInfo.revision || 0
          });
        }
      }

      setTrackingData(data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusUpdate(vendorId, subsystemId, newStatus) {
    try {
      const trackingDocRef = doc(
        db,
        `tenders/${tenderId}/subsystems/${subsystemId}/tracking/${vendorId}`
      );

      const currentData = trackingData.find(
        d => d.vendorId === vendorId && d.subsystemId === subsystemId
      );

      const updateData = {
        status: newStatus,
        lastUpdated: Timestamp.now(),
        notes: notes[`${vendorId}-${subsystemId}`] || currentData?.notes || '',
        revision: currentData?.revision || 0
      };

      // Increment revision if status contains 'revision' or 'resent'
      if (newStatus.includes('revision') || newStatus === 'resent') {
        updateData.revision = (currentData?.revision || 0) + 1;
      }

      await setDoc(trackingDocRef, updateData, { merge: true });
      
      // Refresh data
      fetchTrackingData();
      setEditingStatus(null);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  }

  async function handleNotesUpdate(vendorId, subsystemId, newNotes) {
    try {
      const trackingDocRef = doc(
        db,
        `tenders/${tenderId}/subsystems/${subsystemId}/tracking/${vendorId}`
      );

      await setDoc(
        trackingDocRef,
        {
          notes: newNotes,
          lastUpdated: Timestamp.now()
        },
        { merge: true }
      );

      fetchTrackingData();
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function getStatusColor(statusValue) {
    const status = STATUS_OPTIONS.find(s => s.value === statusValue);
    return status ? status.color : '#9E9E9E';
  }

  function getStatusLabel(statusValue) {
    const status = STATUS_OPTIONS.find(s => s.value === statusValue);
    return status ? status.label : statusValue;
  }

  // Filter data
  const filteredData = trackingData.filter(item => {
    if (filterStatus !== 'all' && item.status !== filterStatus) {
      return false;
    }
    if (filterSubsystem !== 'all' && item.subsystemId !== filterSubsystem) {
      return false;
    }
    return true;
  });

  // Calculate statistics
  const stats = {
    total: trackingData.length,
    notSent: trackingData.filter(d => d.status === 'not_sent').length,
    sent: trackingData.filter(d => d.status === 'sent').length,
    pending: trackingData.filter(d => d.status === 'pending').length,
    replied: trackingData.filter(d => d.status === 'replied').length,
    completed: trackingData.filter(d => d.status === 'completed').length,
    nonResponsive: trackingData.filter(d => d.status === 'non_responsive').length
  };

  return (
    <div className="vendor-tracking-container">
      {/* Statistics Cards */}
      <div className="tracking-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Vendors</div>
        </div>
        <div className="stat-card" style={{ borderColor: '#2196F3' }}>
          <div className="stat-value">{stats.sent}</div>
          <div className="stat-label">Sent</div>
        </div>
        <div className="stat-card" style={{ borderColor: '#FF9800' }}>
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card" style={{ borderColor: '#4CAF50' }}>
          <div className="stat-value">{stats.replied}</div>
          <div className="stat-label">Replied</div>
        </div>
        <div className="stat-card" style={{ borderColor: '#F44336' }}>
          <div className="stat-value">{stats.nonResponsive}</div>
          <div className="stat-label">Non-Responsive</div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="tracking-filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Filter by Subsystem:</label>
          <select value={filterSubsystem} onChange={e => setFilterSubsystem(e.target.value)}>
            <option value="all">All Subsystems</option>
            {subsystems.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tracking Table */}
      {loading ? (
        <div className="loading">Loading tracking data...</div>
      ) : filteredData.length === 0 ? (
        <div className="empty-state">
          <p>No tracking data available</p>
          <p style={{ fontSize: '14px', color: '#666' }}>
            Assign vendors to subsystems and send RFQs to start tracking
          </p>
        </div>
      ) : (
        <div className="tracking-table-container">
          <table className="tracking-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Subsystem</th>
                <th>Status</th>
                <th>Revision</th>
                <th>Last Updated</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={`${item.vendorId}-${item.subsystemId}-${index}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{item.vendorName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{item.vendorEmail}</div>
                  </td>
                  <td>{item.subsystemName}</td>
                  <td>
                    {editingStatus?.vendorId === item.vendorId && editingStatus?.subsystemId === item.subsystemId ? (
                      <select
                        value={item.status}
                        onChange={(e) => handleStatusUpdate(item.vendorId, item.subsystemId, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }}
                        autoFocus
                        onBlur={() => setEditingStatus(null)}
                      >
                        {STATUS_OPTIONS.map(status => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span 
                        className="status-badge" 
                        style={{
                          backgroundColor: `${getStatusColor(item.status)}20`,
                          color: getStatusColor(item.status),
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        onClick={() => setEditingStatus({ vendorId: item.vendorId, subsystemId: item.subsystemId })}
                        title="Click to change status"
                      >
                        {getStatusLabel(item.status)}
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {item.revision > 0 ? (
                      <span style={{ 
                        backgroundColor: '#9C27B020', 
                        color: '#9C27B0', 
                        padding: '2px 8px', 
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        R{item.revision}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ fontSize: '13px' }}>{formatDate(item.lastUpdated)}</td>
                  <td>
                    <input
                      type="text"
                      value={notes[`${item.vendorId}-${item.subsystemId}`] !== undefined 
                        ? notes[`${item.vendorId}-${item.subsystemId}`] 
                        : item.notes
                      }
                      onChange={(e) => setNotes({
                        ...notes,
                        [`${item.vendorId}-${item.subsystemId}`]: e.target.value
                      })}
                      onBlur={(e) => {
                        if (e.target.value !== item.notes) {
                          handleNotesUpdate(item.vendorId, item.subsystemId, e.target.value);
                        }
                      }}
                      placeholder="Add notes..."
                      style={{
                        width: '200px',
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => onUploadQuote && onUploadQuote(item.subsystemId, item.vendorId)}
                      className="action-button"
                      style={{ fontSize: '12px', padding: '4px 12px' }}
                    >
                      Upload Quote
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 10px 0' }}>Status Guide:</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          {STATUS_OPTIONS.map(status => (
            <div key={status.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '16px', 
                height: '16px', 
                backgroundColor: status.color, 
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px' }}>{status.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
